import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Label } from '@/common/ui/label'
import { CalendarIcon, RefreshCwIcon } from 'lucide-react'
import { format } from 'date-fns'
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs'
import { useDebounceValue } from 'usehooks-ts'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import RetentionDateRangeButtons from '../../components/RetentionDateRangeButtons'
import { cn } from '@/common/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Calendar } from '@/common/ui/calendar'
import { useState } from 'react'
import { BucketSize, CohortType } from '@/schema'
import { useQueryGetTTFBDistribution } from '../../api/hooks/useQueryGetTTFBDistribution'
import { useQueryGetTTFDDistribution } from '../../api/hooks/useQueryGetTTFDDistribution'
import { useQueryGetCohortRetention } from '../../api/hooks/useQueryGetCohortRetention'
// import { useQueryGetStickinessTrend } from '../../api/hooks/useQueryGetStickinessTrend'
// import { useQueryGetPlayerLifecycle } from '../../api/hooks/useQueryGetPlayerLifecycle'

import {
	BUCKET_SIZE_OPTIONS,
	COHORT_TYPE_OPTIONS,
	// WINDOW_TYPE_OPTIONS,
	RETENTION_DAYS_OPTIONS,
} from '../../constants'

import TTFBChart from '../../components/TTFBChart'
import TTFDChartSimplified from '../../components/TTFDChartSimplified'
import CohortRetentionHeatmapSimplified from '../../components/CohortRetentionHeatmapSimplified'
// import StickinessChartSimplified from '../../components/StickinessChartSimplified'
// import PlayerLifecycleChartSimplified from '../../components/PlayerLifecycleChartSimplified'

export default function RetentionAnalyticsPage() {
	const [datePickerOpen, setDatePickerOpen] = useState<'start' | 'end' | null>(
		null,
	)

	// Use the same date filter system as OverallAnalyticPage
	const { dateFilter, setDateFilter } = useDateFilter()

	// Individual chart filters
	const [ttfbParams, setttfbParams] = useQueryStates({
		ttfbBucketSize: parseAsString.withDefault('minute'),
	})

	const [ttfdParams, setTtfdParams] = useQueryStates({
		ttfdBucketSize: parseAsString.withDefault('minute'),
	})

	const [cohortParams, setCohortParams] = useQueryStates({
		cohortType: parseAsString.withDefault('daily'),
		retentionDays: parseAsInteger.withDefault(7),
	})

	// const [stickinessParams, setStickinessParams] = useQueryStates({
	// 	windowType: parseAsString.withDefault('daily'),
	// })

	// Debounce dates for API calls
	const [debouncedStartDate] = useDebounceValue(dateFilter.startDate || '', 500)
	const [debouncedEndDate] = useDebounceValue(dateFilter.endDate || '', 500)

	// Prepare API query objects
	const baseQuery = {
		startDate: debouncedStartDate || undefined,
		endDate: debouncedEndDate || undefined,
	}

	const ttfbQuery = {
		...baseQuery,
		bucketSize: ttfbParams.ttfbBucketSize as BucketSize,
	}

	const ttfdQuery = {
		...baseQuery,
		bucketSize: ttfdParams.ttfdBucketSize as BucketSize,
	}

	const cohortQuery = {
		...baseQuery,
		cohortType: cohortParams.cohortType as CohortType,
		retentionDays: cohortParams.retentionDays,
	}

	// const stickinessQuery = {
	// 	...baseQuery,
	// 	windowType: stickinessParams.windowType as CohortType,
	// }

	// API calls
	const ttfbResult = useQueryGetTTFBDistribution(ttfbQuery)
	const ttfdResult = useQueryGetTTFDDistribution(ttfdQuery)
	const cohortResult = useQueryGetCohortRetention(cohortQuery)
	// const stickinessResult = useQueryGetStickinessTrend(stickinessQuery)
	// const lifecycleResult = useQueryGetPlayerLifecycle(baseQuery)

	const isAnyLoading = [
		ttfbResult.isLoading,
		ttfdResult.isLoading,
		cohortResult.isLoading,
		// stickinessResult.isLoading,
		// lifecycleResult.isLoading,
	].some(Boolean)

	const handleDateSelect = (date: Date | undefined, type: 'start' | 'end') => {
		if (date) {
			if (type === 'start') {
				setDateFilter({
					startDate: format(date, 'yyyy-MM-dd'),
					endDate: dateFilter.endDate,
				})
			} else {
				setDateFilter({
					startDate: dateFilter.startDate,
					endDate: format(date, 'yyyy-MM-dd'),
				})
			}
		}
		setDatePickerOpen(null)
	}

	const handleRefresh = () => {
		// Trigger refetch for all queries
		ttfbResult.refetch()
		ttfdResult.refetch()
		cohortResult.refetch()
		// stickinessResult.refetch()
		// lifecycleResult.refetch()
	}

	return (
		<div className="space-y-6 p-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						วิเคราะห์ Retention
					</h1>
					<p className="text-muted-foreground">
						วิเคราะห์พฤติกรรมการยึดติดและการกลับมาเล่นของผู้เล่น
					</p>
				</div>
				<Button
					onClick={handleRefresh}
					disabled={isAnyLoading}
					variant="outline"
				>
					<RefreshCwIcon
						className={cn('mr-2 h-4 w-4', isAnyLoading && 'animate-spin')}
					/>
					รีเฟรช
				</Button>
			</div>

			{/* Global Filters */}
			<Card className="border-2">
				<CardHeader>
					<CardTitle>ช่วงวันที่ (ใช้ร่วมกันทุกกราฟ)</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{/* Date Range Buttons */}
						<div>
							<Label className="mb-2 block text-sm font-medium">ช่วงเวลา</Label>
							<RetentionDateRangeButtons />
						</div>

						{/* Custom Date Range */}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label>วันที่เริ่มต้น (กำหนดเอง)</Label>
								<Popover
									open={datePickerOpen === 'start'}
									onOpenChange={() =>
										setDatePickerOpen(
											datePickerOpen === 'start' ? null : 'start',
										)
									}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												'w-full justify-start text-left font-normal',
												!dateFilter.startDate && 'text-muted-foreground',
											)}
										>
											<CalendarIcon className="mr-2 h-4 w-4" />
											{dateFilter.startDate
												? format(new Date(dateFilter.startDate), 'dd/MM/yyyy')
												: 'เลือกวันที่'}
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0" align="start">
										<Calendar
											mode="single"
											selected={
												dateFilter.startDate
													? new Date(dateFilter.startDate)
													: undefined
											}
											onSelect={(date) => handleDateSelect(date, 'start')}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							</div>

							<div className="space-y-2">
								<Label>วันที่สิ้นสุด (กำหนดเอง)</Label>
								<Popover
									open={datePickerOpen === 'end'}
									onOpenChange={() =>
										setDatePickerOpen(datePickerOpen === 'end' ? null : 'end')
									}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												'w-full justify-start text-left font-normal',
												!dateFilter.endDate && 'text-muted-foreground',
											)}
										>
											<CalendarIcon className="mr-2 h-4 w-4" />
											{dateFilter.endDate
												? format(new Date(dateFilter.endDate), 'dd/MM/yyyy')
												: 'เลือกวันที่'}
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0" align="start">
										<Calendar
											mode="single"
											selected={
												dateFilter.endDate
													? new Date(dateFilter.endDate)
													: undefined
											}
											onSelect={(date) => handleDateSelect(date, 'end')}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Charts Grid */}
			<div className="space-y-6">
				{/* TTFB Distribution */}
				<Card>
					<CardHeader className="pb-3">
						<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
							<CardTitle>Time to First Spin (TTFB)</CardTitle>
							<div className="flex items-center gap-2">
								<Label className="text-sm">ขนาดช่วงเวลา:</Label>
								<Select
									value={ttfbParams.ttfbBucketSize}
									onValueChange={(value) =>
										setttfbParams({ ttfbBucketSize: value })
									}
								>
									<SelectTrigger className="w-32">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{BUCKET_SIZE_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<TTFBChart
							data={ttfbResult.data}
							isLoading={ttfbResult.isLoading}
						/>
					</CardContent>
				</Card>

				{/* TTFD Distribution */}
				<Card>
					<CardHeader className="pb-3">
						<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
							<CardTitle>Time to First Deposit (TTFD)</CardTitle>
							<div className="flex items-center gap-2">
								<Label className="text-sm">ขนาดช่วงเวลา:</Label>
								<Select
									value={ttfdParams.ttfdBucketSize}
									onValueChange={(value) =>
										setTtfdParams({ ttfdBucketSize: value })
									}
								>
									<SelectTrigger className="w-32">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{BUCKET_SIZE_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<TTFDChartSimplified
							data={ttfdResult.data}
							isLoading={ttfdResult.isLoading}
						/>
					</CardContent>
				</Card>

				{/* Stickiness Trend (DAU/MAU) - commented out, not in use */}
				{/*
				<Card>
					<CardHeader className="pb-3">
						<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
							<CardTitle>Stickiness Trend (DAU/MAU)</CardTitle>
							<div className="flex items-center gap-2">
								<Label className="text-sm">หน้าต่างเวลา:</Label>
								<Select
									value={stickinessParams.windowType}
									onValueChange={(value) =>
										setStickinessParams({ windowType: value })
									}
								>
									<SelectTrigger className="w-32">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{WINDOW_TYPE_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<StickinessChartSimplified
							data={stickinessResult.data}
							isLoading={stickinessResult.isLoading}
						/>
					</CardContent>
				</Card>
				*/}

				{/* Player Lifecycle Analysis - commented out, not in use */}
				{/*
				<Card>
					<CardHeader className="pb-3">
						<CardTitle>Player Lifecycle Analysis</CardTitle>
					</CardHeader>
					<CardContent>
						<PlayerLifecycleChartSimplified
							data={lifecycleResult.data}
							isLoading={lifecycleResult.isLoading}
						/>
					</CardContent>
				</Card>
				*/}

				{/* Cohort Retention Heatmap */}
				<Card>
					<CardHeader className="pb-3">
						<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
							<CardTitle>Cohort Retention Heatmap</CardTitle>
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									<Label className="text-sm">ประเภท Cohort:</Label>
									<Select
										value={cohortParams.cohortType}
										onValueChange={(value) =>
											setCohortParams({ cohortType: value })
										}
									>
										<SelectTrigger className="w-32">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{COHORT_TYPE_OPTIONS.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="flex items-center gap-2">
									<Label className="text-sm">วันติดตาม:</Label>
									<Select
										value={cohortParams.retentionDays.toString()}
										onValueChange={(value) =>
											setCohortParams({ retentionDays: parseInt(value) })
										}
									>
										<SelectTrigger className="w-24">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{RETENTION_DAYS_OPTIONS.map((option) => (
												<SelectItem
													key={option.value}
													value={option.value.toString()}
												>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<CohortRetentionHeatmapSimplified
							data={cohortResult.data}
							isLoading={cohortResult.isLoading}
						/>
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
